import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui";
import type { Location } from "../../types/Location";
import type { SetupStep } from "./useHomeData";

const portal: unknown = import.meta.env.VITE_CUSTOMER_PORTAL_URL;

/** Shown until every step is done. */
export const SetupChecklist = ({
  steps,
  locations,
}: {
  steps: SetupStep[];
  locations: Location[];
}) => {
  const { t } = useTranslation("home");
  const first = locations[0];
  const done = steps.filter((s) => s.done).length;
  const guestUrl =
    typeof portal === "string" && first
      ? `${portal.replace(/\/$/, "")}/${first.id}`
      : null;

  const target: Record<SetupStep["id"], string | null> = {
    location: "/locations/create",
    menu: "/menus/create",
    tables: first ? `/locations/${first.id}` : "/locations/create",
    order: guestUrl,
  };

  return (
    <section aria-labelledby="setup-title">
      <Card>
        <h2 id="setup-title" className="text-title text-strong">
          {t("setup.title", { done, total: steps.length })}
        </h2>
        <ul className="mt-3 grid gap-1 sm:grid-cols-2">
          {steps.map((s) => {
            const href = target[s.id];
            const label = t(`setup.${s.id}`);
            return (
              <li
                key={s.id}
                className="flex min-h-11 items-center gap-3 text-small text-strong"
              >
                {s.done ? (
                  <CheckCircleIcon
                    className="size-6 shrink-0 text-tone-success-fg"
                    aria-hidden="true"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="size-6 shrink-0 rounded-chip border-[1.5px] border-line-strong"
                  />
                )}
                {s.done || !href ? (
                  <span className={s.done ? "text-muted" : ""}>
                    {s.done && (
                      <span className="sr-only">{t("setup.done")} </span>
                    )}
                    {label}
                  </span>
                ) : href.startsWith("/") ? (
                  <Link
                    to={href}
                    className="text-link underline underline-offset-2"
                  >
                    {label}
                  </Link>
                ) : (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link underline underline-offset-2"
                  >
                    {label}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
    </section>
  );
};
